/**
 * The three things a sheet can hold besides the selection: tables to add, the
 * guest list, and the plan itself (its numbers, what the PDF prints, the
 * backup). Along the bottom of a phone, where a thumb rests; in the top bar on
 * a phone held sideways, where the height belongs to the room.
 *
 * Pressing the panel already open closes it — the bar is the sheet's switch as
 * much as its menu.
 */

import type { Plan } from '../lib/types'
import { useT } from '../lib/i18n/I18nProvider'
import { type EditorPanel, useViewStore } from '../store/viewStore'

interface Props {
  plan: Plan
  placement: 'bottom' | 'top'
}

export function PanelBar({ plan, placement }: Props) {
  const t = useT()
  const panel = useViewStore((s) => s.panel)

  const toggle = (next: EditorPanel) => {
    const view = useViewStore.getState()
    if (view.panel === next) view.closePanel()
    else view.openPanel(next)
  }

  const items: Array<{ id: EditorPanel; label: string; icon: JSX.Element; count?: number }> = [
    { id: 'tables', label: t.rail.tabTables, icon: TABLES_ICON },
    { id: 'guests', label: t.rail.tabGuests, icon: GUESTS_ICON, count: plan.guests.length },
    { id: 'plan', label: t.panels.plan, icon: PLAN_ICON },
  ]

  return (
    <div className={`panelbar panelbar--${placement}`}>
      {items.map((item) => (
        <button
          key={item.id}
          type="button"
          className={`panelbar-btn${panel === item.id ? ' is-active' : ''}`}
          onClick={() => toggle(item.id)}
          aria-expanded={panel === item.id}
          aria-controls="editor-sheet"
        >
          {item.icon}
          <span className="panelbar-label">{item.label}</span>
          {item.count ? <span className="mono panelbar-count">{item.count}</span> : null}
        </button>
      ))}
    </div>
  )
}

const STROKE = {
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.6,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
} as const

/** A round table from above with four chairs — the brand mark, in one ink. */
const TABLES_ICON = (
  <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
    <circle cx="12" cy="12" r="5" {...STROKE} />
    <circle cx="12" cy="3.6" r="1.6" fill="currentColor" />
    <circle cx="20.4" cy="12" r="1.6" fill="currentColor" />
    <circle cx="12" cy="20.4" r="1.6" fill="currentColor" />
    <circle cx="3.6" cy="12" r="1.6" fill="currentColor" />
  </svg>
)

const GUESTS_ICON = (
  <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
    <circle cx="9" cy="8.5" r="3.2" {...STROKE} />
    <path d="M3.5 19.5c.6-3.2 2.8-5 5.5-5s4.9 1.8 5.5 5" {...STROKE} />
    <path d="M15.2 5.6a3.2 3.2 0 0 1 0 5.8M17.2 14.8c1.7.7 2.9 2.3 3.3 4.7" {...STROKE} />
  </svg>
)

/** A sheet with ticks: what is ready to print. */
const PLAN_ICON = (
  <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
    <rect x="5" y="3.5" width="14" height="17" rx="2" {...STROKE} />
    <path d="M8.5 9.5l1.4 1.4 2.6-2.8M8.5 15l1.4 1.4 2.6-2.8M14.5 10h1.5M14.5 15.5h1.5" {...STROKE} />
  </svg>
)
